// Scale bridge dashboard - shared formatters.

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);

export { dayjs };

/**
 * A weight, for display.
 *
 * null is NOT zero and must never render as "0 kg". The firmware sends null when
 * it could not parse the number off the label, and zero is a real weight the
 * machine reports - conflating them puts a wrong figure in front of somebody.
 * Callers show `totalNetWeightRaw` alongside when this returns the dash.
 */
export function weightKg(value: number | null | undefined): string {
    if (typeof value !== 'number') return '—';
    return `${Number(value.toFixed(3)).toLocaleString('en-IN')} kg`;
}

/** Totals for the KPI tiles, where a fractional kilogram is noise. */
export function weightTotal(value: number): string {
    return `${Math.round(value).toLocaleString('en-IN')} kg`;
}

export function timestamp(iso: string | null | undefined): string {
    if (!iso) return '—';
    const d = dayjs(iso);
    return d.isValid() ? d.format('DD MMM YYYY, hh:mm A') : String(iso);
}

/**
 * The clock time alone, for the weighings table.
 *
 * A table sorted newest-first is mostly today, and repeating "08 Sep 2026" down
 * every row spends the widest column restating the one thing every row agrees
 * on. The time is what distinguishes the rows, so the time leads and dayLabel()
 * carries the date underneath it.
 */
export function clockTime(iso: string | null | undefined): string {
    if (!iso) return '—';
    const d = dayjs(iso);
    return d.isValid() ? d.format('hh:mm A') : String(iso);
}

/**
 * The date, said the way somebody standing at the weighbridge would say it.
 *
 * "Today" and "Yesterday" are named rather than dated because those are the two
 * days an operator is nearly always looking at, and a reader should not have to
 * compare a printed date against the calendar to work out whether a row is from
 * this shift.
 */
export function dayLabel(iso: string | null | undefined): string {
    if (!iso) return '—';
    const d = dayjs(iso);
    if (!d.isValid()) return String(iso);

    const today = dayjs().startOf('day');
    const day = d.startOf('day');
    const diff = day.diff(today, 'day');
    if (diff === 0) return 'Today';
    if (diff === -1) return 'Yesterday';
    return d.format('DD MMM YYYY');
}

/** True when the printer sent nothing for a field. See orDash. */
export function isBlank(value: string | null | undefined): boolean {
    return (value ?? '').trim() === '';
}

export function relative(iso: string | null | undefined): string {
    if (!iso) return 'never';
    const d = dayjs(iso);
    return d.isValid() ? d.fromNow() : String(iso);
}

/** A field the printer left blank is normal - the captured label prints its batch
 *  number as underscores. Show a dash rather than an empty cell that reads as a
 *  rendering fault. */
export function orDash(value: string | null | undefined): string {
    const s = (value ?? '').trim();
    return s === '' ? '—' : s;
}

/** A device that has not been heard from in this long is shown as stale. Two
 *  minutes is several times the interval between weighings on a busy day. */
export const DEVICE_STALE_MS = 2 * 60 * 1000;

export function isStale(iso: string | null | undefined): boolean {
    if (!iso) return true;
    return Date.now() - +new Date(iso) > DEVICE_STALE_MS;
}
